import { useLocalObservable } from 'mobx-react-lite';
import { useEffect, useRef, MutableRefObject } from 'react';
import { useLocation } from 'react-router-dom';

import Tasks from 'APP/Tasks';
import { updateMediaGallery } from 'APP/Tasks/group/mediaGallery/updateMediaGallery';
import { PayloadType } from 'APP/model/message/messageModel.types';
import { RouterState } from 'APP/router/constants';
import { IMediaGalleryState } from 'APP/types/mediaGallery';
import Entities from 'STORE';
import { Group } from 'STORE/Groups/Group';

import {
  IMediaGalleryFile,
  IMediaGalleryPreviewFiles,
  TSetSidebarVisabilityHandler,
  TCloseHandler,
  TSharedMediaItem,
} from './MediaGallery.types';

interface IGetNeighbourMessageFileParams {
  items: TSharedMediaItem[];
  currentIndex: number;
  currentMessageId: string;
}

interface IMediaGalleryPresenter {
  _isShowSidebar: boolean;
  galleryState: IMediaGalleryState | null;
  currentFile: TSharedMediaItem | null;
  nextFile: TSharedMediaItem | null;
  nextMessageFile: TSharedMediaItem | null;
  prevFile: TSharedMediaItem | null;
  prevMessageFile: TSharedMediaItem | null;
  isOpened: boolean;
  mustBeClosed: boolean;
  isLoading: boolean;
  isExistSidebar: boolean;
  isShowSidebar: boolean;
  focusMessageId: string | null;
  group: Group | null;
  file: IMediaGalleryFile | null;
  albumPreviewFiles: IMediaGalleryPreviewFiles | null;
  onSetSidebarVisibility: TSetSidebarVisabilityHandler;
  onClose: TCloseHandler;
  setGalleryQueue(): void;
  setGalleryState(state: IMediaGalleryState | null): void;
  getNeighbourMessageFile(
    params: IGetNeighbourMessageFileParams,
    isPrev?: boolean
  ): TSharedMediaItem | null;
}

const GALLERY_PREVIEW_TYPES = [PayloadType.Album, PayloadType.Article];

export const useMediaGalleryPresenter = (): {
  presenter: IMediaGalleryPresenter;
  containerRef: MutableRefObject<HTMLDivElement | null>;
} => {
  const containerRef: MutableRefObject<HTMLDivElement | null> = useRef(null);
  const location = useLocation();

  const presenter = useLocalObservable<IMediaGalleryPresenter>(() => ({
    _isShowSidebar: true,
    galleryState: null,
    currentFile: null,
    nextFile: null,
    nextMessageFile: null,
    prevFile: null,
    prevMessageFile: null,

    get isOpened(): boolean {
      if (presenter.galleryState) {
        return (
          !!presenter.galleryState.groupId &&
          !!presenter.galleryState.mediaId &&
          !!presenter.galleryState.messageId
        );
      }
      return false;
    },

    get mustBeClosed(): boolean {
      return !presenter.file && !presenter.galleryState?.mediaId && !presenter.isLoading;
    },

    get isLoading(): boolean {
      if (presenter.file) {
        return false;
      }

      if (presenter.group?.sharedData.media) {
        return presenter.group.sharedData.media.isLoading;
      }

      return true;
    },

    get isExistSidebar(): boolean {
      return !!presenter.galleryState?.showMessageInfo;
    },

    get isShowSidebar(): boolean {
      return presenter.isExistSidebar && presenter._isShowSidebar;
    },

    get focusMessageId(): string | null {
      return Entities.GroupsStore.activeGroup?.messagesStore.focusedMessageId || null;
    },

    get group(): Group | null {
      if (!presenter.galleryState?.groupId) {
        return null;
      }

      return Entities.GroupsStore.getGroupById(presenter.galleryState.groupId);
    },

    get file(): IMediaGalleryFile | null {
      if (!presenter.currentFile) {
        return null;
      }

      const message = presenter.group?.sharedData.getMessageById(presenter.currentFile.messageId);

      if (!message) {
        return null;
      }

      return {
        ...presenter.currentFile,
        nextFile: presenter.nextFile,
        prevFile: presenter.prevFile,
        message,
      };
    },

    get albumPreviewFiles(): IMediaGalleryPreviewFiles | null {
      if (!presenter.file || !presenter.group) {
        return null;
      }

      const message = presenter.group?.sharedData?.getMessageById(presenter.file.messageId);

      if (!message) {
        return null;
      }

      if (GALLERY_PREVIEW_TYPES.includes(message.payload.payloadType)) {
        let items = presenter.group.sharedData.media.items
          .filter((item) => item.messageId === presenter.file?.messageId)
          .map((item) => ({ ...item, message }));

        if (presenter.galleryState?.reverse) {
          items = items.reverse();
        }

        const currentIndex = items.findIndex((item) => item.id === presenter.file?.id);
        const leftFiles = items.slice(0, currentIndex);
        const rightFiles = items.slice(currentIndex + 1);

        return { leftFiles, rightFiles };
      }

      return null;
    },

    onSetSidebarVisibility(isVisible: boolean): void {
      presenter._isShowSidebar = isVisible;
    },

    onClose(): void {
      Tasks.group.closeMediaGallery();
      presenter._isShowSidebar = true;
    },

    setGalleryQueue(): void {
      if (
        !presenter.group ||
        !presenter.galleryState?.messageId ||
        !presenter.galleryState?.mediaId
      ) {
        presenter.currentFile = null;
        return;
      }

      if (!presenter.group.sharedData.media.items.length) {
        presenter.currentFile = null;
        presenter.nextFile = null;
        presenter.prevFile = null;
        return;
      }

      const messageIds = presenter.galleryState?.messageIds || [];

      // get real message id
      const messageId = presenter.group.messagesStore.getMessageById(
        presenter.galleryState?.messageId
      )?.id;
      const mediaId = presenter.galleryState?.mediaId;

      const items = presenter.group.sharedData.media.items.filter((item) => {
        return !messageIds.length || messageIds.includes(item.messageId);
      });

      const currentIndex = items.findIndex(
        (item) => item.id === mediaId && item.messageId === messageId
      );

      const currentFile = items[currentIndex];

      if (!currentFile) {
        presenter.currentFile = null;
        //  Check that prevFile and nextFile still exists in array of items
        //  if not - swap them with fallbacks prevMessageFile and nextMessageFile
        if (!items.find((item) => item.id === presenter.prevFile?.id)) {
          presenter.prevFile = presenter.prevMessageFile;
        }
        if (!items.find((item) => item.id === presenter.nextFile?.id)) {
          presenter.nextFile = presenter.nextMessageFile;
        }

        return;
      }

      presenter.currentFile = currentFile;
      presenter.prevFile = items[currentIndex - 1] || null;
      presenter.nextFile = items[currentIndex + 1] || null;

      //  In case when a message with album could be removed with prevFile and nextFile
      //  we need a fallback to next or prev messages files
      const neighbourMessageFileParams = {
        items,
        currentIndex,
        currentMessageId: messageId,
      };

      presenter.prevMessageFile =
        presenter.prevFile?.messageId === messageId
          ? presenter.getNeighbourMessageFile(neighbourMessageFileParams, true)
          : presenter.prevFile;
      presenter.nextMessageFile =
        presenter.nextFile?.messageId === messageId
          ? presenter.getNeighbourMessageFile(neighbourMessageFileParams)
          : presenter.nextFile;
    },

    getNeighbourMessageFile(
      params: IGetNeighbourMessageFileParams,
      isPrev: boolean
    ): TSharedMediaItem | null {
      if (!params.items.length || params.currentIndex < 0) {
        return null;
      }
      const itemsPart = isPrev
        ? params.items.slice(0, params.currentIndex - 1).reverse()
        : params.items.slice(params.currentIndex + 1);
      return itemsPart.find((item) => item.messageId !== params.currentMessageId) || null;
    },

    setGalleryState(state: IMediaGalleryState | null): void {
      presenter.galleryState = state;
    },
  }));

  useEffect(() => {
    presenter.setGalleryState(
      (location.state?.[RouterState.mediaGallery] as IMediaGalleryState) || null
    );
  }, [location.state]);

  useEffect(() => {
    presenter.setGalleryQueue();
  }, [presenter.galleryState, presenter.group?.sharedData.media.items]);

  useEffect(() => {
    if (!presenter.file && presenter.galleryState?.mediaId) {
      const otherFile = presenter.galleryState.reverse
        ? presenter.prevFile || presenter.nextFile
        : presenter.nextFile || presenter.prevFile;

      if (!otherFile) {
        presenter.onClose();
        return;
      }

      updateMediaGallery({
        groupId: otherFile.groupId,
        messageId: otherFile.messageId,
        mediaId: otherFile.id,
      });
    }
  }, [presenter.file, presenter.galleryState?.mediaId]);

  useEffect(() => {
    if (presenter.isOpened && containerRef.current) {
      containerRef.current.focus();
    }
  }, [presenter.isOpened]);

  useEffect(() => {
    if (presenter.focusMessageId && presenter.isOpened) {
      presenter.onClose();
    }
  }, [presenter.focusMessageId]);

  return { presenter, containerRef };
};
